const keptTags = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'A', 'UL', 'OL', 'LI'])
const droppedTags = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'IFRAME', 'OBJECT', 'EMBED', 'NOSCRIPT', 'SVG', 'MATH', 'TITLE'])
const blockTags = new Set(['P', 'UL', 'OL', 'LI'])
const unsafeHref = /^(javascript|data|vbscript):/i

interface SanitizeOptions {
    inline?: boolean
}

const escapeHtml = (text: string) => text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

export const isRichTextHtml = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value)

export const plainTextToRichText = (text: string) => `<p>${escapeHtml(text).replace(/\n/g, '<br>')}</p>`

const isSafeHref = (href: string) => !unsafeHref.test(href.replace(/[\s\u0000-\u001f]/g, ''))

const copyChildren = (source: Node, target: Node, doc: Document, options: SanitizeOptions) => {
    source.childNodes.forEach(child => {
        const clean = sanitizeNode(child, doc, options)
        if (clean) target.appendChild(clean)
    })
}

const sanitizeNode = (node: Node, doc: Document, options: SanitizeOptions): Node | null => {
    if (node.nodeType === Node.TEXT_NODE) return doc.createTextNode(node.textContent ?? '')
    if (node.nodeType !== Node.ELEMENT_NODE) return null

    const element = node as Element
    const tag = element.tagName.toUpperCase()
    if (droppedTags.has(tag)) return null

    const fragment = doc.createDocumentFragment()
    if (!keptTags.has(tag) || (options.inline && blockTags.has(tag))) {
        copyChildren(element, fragment, doc, options)
        if (options.inline && blockTags.has(tag) && element.nextElementSibling) {
            fragment.appendChild(doc.createElement('br'))
        }
        return fragment
    }

    if (tag === 'A') {
        const href = element.getAttribute('href')?.trim() ?? ''
        if (!href || !isSafeHref(href)) {
            copyChildren(element, fragment, doc, options)
            return fragment
        }
        const link = doc.createElement('a')
        link.setAttribute('href', href)
        link.setAttribute('data-action', 'url')
        link.setAttribute('data-url', href)
        copyChildren(element, link, doc, options)
        return link
    }

    const clean = doc.createElement(tag.toLowerCase())
    const start = element.getAttribute('start')
    if (tag === 'OL' && start && /^\d+$/.test(start)) clean.setAttribute('start', start)
    copyChildren(element, clean, doc, options)
    if (tag === 'P' && !clean.hasChildNodes()) clean.appendChild(doc.createElement('br'))
    return clean
}

export const sanitizeRichText = (value: string, options: SanitizeOptions = {}) => {
    const source = new DOMParser().parseFromString(isRichTextHtml(value) ? value : plainTextToRichText(value), 'text/html')
    const doc = document.implementation.createHTMLDocument('')
    const container = doc.createElement('div')
    copyChildren(source.body, container, doc, options)
    return container.innerHTML
}
